import { CircularProgress, Typography } from "@material-ui/core/";
import { Services as sdkService } from "coolremote-sdk";
import { Button, IconButton, FilterRequire } from '@coolremote/shared';
import { FilterList } from "@material-ui/icons";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { t } from "ttag";
import { TopBar } from "../../components";
import DatePicker from "../../components/MobileDatePicker/MobileDatePicker";
import { Lookup } from "../../components/MobileLookup";
import { AlertBell, ArrowBack, ArrowDown, Close, MobileLogo } from "../../icons/";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import AlertInfo from "./AlertInfo";
import useStyles from "./Alerts.style";
import { getUtcRange } from "@services/timeService";
import NoAlerts from "./MobileNoAlerts";
import { PUSH_RECEIVED_EVENT } from "../../components/PushNotificationsGate";

const MAX_NOTIFICATION_PARSE_RETRIES = 3;

const Alerts: React.FC<any> = (props: any) => {
  const history = useHistory();
  const location: any = useLocation();
  const match: any = useRouteMatch();
  const emptyFilters = { code: {}, status: {}, type: {}, description: {}, priority: {}, category: {} };
  const { state } = location;
  const getSite = useStoreState((s) => s.sites.getSite);
  const allSites = useStoreState((s) => s.sites.allSites);
  const selectedAlertObj = useStoreState((state) => state.selectedAlert);
  const allUnits = useStoreState((state) => state.units.allUnits);
  const errorTypes = useStoreState((state) => state.serviceErrorTypes);
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const [descriptionMap, setDescriptionMap] = useState<any>({});
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const classes = useStyles();
  const types = useStoreState((state) => state.types);
  const { eventTypes, eventStatusTypes, trapPriorityTypes } = types;
  const getAlertsByFilters = useStoreActions((action) => action.alerts.getAlertsByFilters);
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<any>(state?.filters ? state?.filters :emptyFilters);
  const { dateRange } = useStoreState((s) => s.selections.selections);
  const selections = useStoreState((s) => s.selections.mobileSelections);
  const { siteId: selectedSiteId } = selections;
  const [filteredAlerts, setFilteredAlerts] = useState<any>([]);
  const [errorCodesMap, setErrorCodesMap] = useState<any>([]);
  const [alertTypeMap, setAlertTypeMap] = useState<any>([]);
  const [statusesMap, setStatusesMap] = useState<any>([]);
  const [priorityMap, setPriorityMap] = useState<any>([]);
  const [categoryMap, setCategoryMap] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  const [notificationRefresh, setNotificationRefresh] = useState<number>(0);
  const [opendatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [openFilters, setOpenedFilters] = useState<boolean>(false);
  const fetchSiteUnits = useStoreActions((action) => action.units.fetchSiteUnits);

  const setServiceErrorTypes = useStoreActions((action) => action.setServiceErrorTypes);
  const stillLoadingInitialAPIs = useStoreState((state) => state.stillLoadingInitialAPIs);
  const getNotificationAlerts = useStoreActions((action) => action.alerts.getNotificationAlerts);
  const updateMobileSelections = useStoreActions((a) => a.selections.updateMobileSelections);
  const notificationParseRetryRef = useRef({ shortId: null as string | null, count: 0 });

  // A push that arrives while this screen is open must show up in the list,
  // which otherwise loads only once (CM-893).
  useEffect(() => {
    const reload = () => setNotificationRefresh((r) => r + 1);
    window.addEventListener(PUSH_RECEIVED_EVENT, reload);
    return () => window.removeEventListener(PUSH_RECEIVED_EVENT, reload);
  }, []);

  // Load site data when siteId changes (similar to control page)
  useEffect(() => {
    const loadSiteData = async () => {
      if (!selectedSiteId) {
        return;
      }

      try {
        await Promise.all([
          fetchSiteUnits(selectedSiteId),
        ]);
      } catch (error) {
        console.error('Failed to fetch site data:', error);
        addMessage({ message: 'Failed to load site data' });
      }
    };

    loadSiteData();
  }, [selectedSiteId]);

  // Helper function to populate filter arrays from alerts data
  const populateFilterArrays = (alerts: any[]) => {
    const errorCodesArray: any[] = [];
    const alertTypeArray: any[] = [];
    const descriptionArray: any[] = [];
    const priorityArray: any[] = [];
    const statusesArray: any[] = [];
    const categoryArray: any[] = [];

    alerts.forEach((alert: any) => {
      if (alert.errorCode && !errorCodesArray.includes(alert.errorCode)) {
        errorCodesArray.push(alert.errorCode);
      }
      if (alert.alertType && !alertTypeArray.includes(alert.alertType)) {
        alertTypeArray.push(alert.alertType);
      }
      if (alert.description && !descriptionArray.includes(alert.description)) {
        descriptionArray.push(alert.description);
      }
      if (alert.priority && !priorityArray.includes(alert.priority)) {
        priorityArray.push(alert.priority);
      }
      if (alert.status && !statusesArray.includes(alert.status)) {
        statusesArray.push(alert.status);
      }
      if (alert.category && !categoryArray.includes(alert.category)) {
        categoryArray.push(alert.category);
      }
    });

    setErrorCodesMap(errorCodesArray.sort());
    setStatusesMap(statusesArray.sort());
    setAlertTypeMap(alertTypeArray.sort());
    setDescriptionMap(descriptionArray.sort());
    setPriorityMap(priorityArray.sort());
    setCategoryMap(categoryArray.sort());
  };

  
  useEffect(() => {

    if (match?.params?.notificationShortId) {
      const shortId = match?.params?.notificationShortId;
      getNotificationAlerts({ notificationShortId: shortId })
        .then((resp: any) => {
          const { parsed, rawAlerts } = resp;
          const alertsData = Object.values(parsed);
          const rawAlertValues = Object.values(rawAlerts) as any[];

          if (rawAlertValues.length === 0) {
            notificationParseRetryRef.current = { shortId: null, count: 0 };
            setRows([]);
            populateFilterArrays([]);
            return;
          }

          const firstRaw = rawAlertValues[0] as any;
          const rawSiteId = firstRaw.site;
          if (rawSiteId && rawSiteId !== selectedSiteId) {
            const site = allSites[rawSiteId];
            updateMobileSelections({ customerId: site?.customer || selections.customerId, siteId: rawSiteId, unitId: null });
          }

          // Auto-select site from notification alert if not already selected
          if (alertsData.length > 0 && !selectedSiteId) {
            const firstAlert = alertsData[0] as any;
            const alertSiteId = firstAlert.alertItemContainerIds?.siteId || firstAlert.siteId;
            if (alertSiteId) {
              const site = allSites[alertSiteId];
              updateMobileSelections({ customerId: site?.customer || selections.customerId, siteId: alertSiteId, unitId: null });
            }
          }

          if (alertsData.length > 0) {
            notificationParseRetryRef.current = { shortId: null, count: 0 };
            setRows(alertsData);
            populateFilterArrays(alertsData);
          } else {
            setRows([]);
            populateFilterArrays([]);
            if (notificationParseRetryRef.current.shortId !== shortId) {
              notificationParseRetryRef.current = { shortId, count: 0 };
            }
            if (notificationParseRetryRef.current.count < MAX_NOTIFICATION_PARSE_RETRIES) {
              notificationParseRetryRef.current.count += 1;
              setTimeout(() => setNotificationRefresh((r: number) => r + 1), 1500);
            }
          }
        })
        .catch((err: any) => {
          notificationParseRetryRef.current = { shortId: null, count: 0 };
          addMessage({ message: err.message });
        })
        .finally(() => setIsDisabled(false));
      return;
    }




    (async () => {
      setIsDisabled(true);
      if(!selectedSiteId) {
        setIsDisabled(false);
        return;
      }
      if (stillLoadingInitialAPIs) {
        return;
      }
      setRows([]);
      if (_.isEmpty(errorTypes)) {
        const errors = await sdkService.getServiceErrorTypes();
        setServiceErrorTypes(errors);
      }


      let startTime = 0;
      let endTime = 0;
      if (dateRange) {
        const timezone = getSite(selectedSiteId)?.timezone || moment.tz.guess();
         // Calculate UTC start and end dates
         const range = getUtcRange(
          dateRange?.startDate,
          dateRange?.endDate,
          timezone
        );
        startTime = range.startTime;
        endTime = range.endTime;
      }

      const allAlertsByTime = !dateRange
        ? await getAlertsByFilters({
            status: [
              eventStatusTypes['open'],
              eventStatusTypes['acknowledged'],
            ],
            openAlertsOnly: false,
          })
        : await getAlertsByFilters({
            startTime,
            endTime,
            withAllOpenAlerts: false,
          });

      const sortedAlerts = _.orderBy(allAlertsByTime, ["eventTime"], ["desc"]);
      
      // Populate filter arrays for regular alerts
      populateFilterArrays(sortedAlerts);
      
      setRows(sortedAlerts);
      setIsDisabled(false);
    })();
  }, [dateRange, stillLoadingInitialAPIs, match?.params?.notificationShortId, selectedSiteId, notificationRefresh, allSites, selections.customerId]);

  useEffect(() => {
    if (!rows.length) {
      setFilteredAlerts([]);
      return;
    }
    const filteredAlerts = getFilteredAlerts(rows);
    setFilteredAlerts(filteredAlerts);
  }, [selections, selectedFilters, rows]);

  useEffect(() => {
    if (!_.isEmpty(selectedAlertObj) && !_.isEmpty(filteredAlerts)) {

      const alertIndex = _.findIndex(filteredAlerts, { id: selectedAlertObj.id });
      setSelectedAlert(filteredAlerts[alertIndex]);
    }
  }, [filteredAlerts]);

  useEffect(() => {
    if (selectedAlert) {
      const updated = filteredAlerts.find((a: any) => a.id === selectedAlert.id);
      if (updated) {
        setSelectedAlert(updated);
      }
    }
  }, [filteredAlerts]);

  const onRowSelect = (alert: any) => {
    setSelectedAlert(alert);
    setOpen(true);
  },
    setAcknowledged = (ackAlert: any) => {
      const changedAckAlerts = filteredAlerts.map((alert: any) => {
        let selectedAlert = null;
        if (alert.id === ackAlert.id) {
          alert.status = ackAlert.data ? "Acknowledged" : "Open";
          selectedAlert = alert;
        }
        return alert;
      });

      setFilteredAlerts(getFilteredAlerts(changedAckAlerts));
      setSelectedAlert(selectedAlert);
    };

  const updateAlertLocally = (alertToUpdate: any, data: any) => {
    const isSnoozeUpdate = "snoozeUsername" in data;
    const scope = data.snoozeScope || alertToUpdate.snoozeScope;
    const targetSystemId = data.snoozeSystemId || alertToUpdate.snoozeSystemId || alertToUpdate.systemId || alertToUpdate.alertItemContainerIds?.systemId;
    const targetUnitId = data.snoozeUnitId || alertToUpdate.snoozeUnitId || alertToUpdate.unitId || alertToUpdate.alertItemContainerIds?.unitId;

    const matchesSnoozeTarget = (a: any) => {
      if (!isSnoozeUpdate) {
        return a.id === alertToUpdate.id;
      }
      if (scope === "system" && targetSystemId) {
        const aSysId = a.systemId || a.alertItemContainerIds?.systemId;
        const aSysIds = a.systemIds || a.alertItemContainerIds?.systemIds || [];
        return aSysId === targetSystemId || aSysIds.includes(targetSystemId);
      }
      if (scope === "unit" && targetUnitId) {
        const aUnitId = a.unitId || a.alertItemContainerIds?.unitId;
        const aUnitIds = a.unitIds || a.alertItemContainerIds?.unitIds || [];
        return aUnitId === targetUnitId || aUnitIds.includes(targetUnitId);
      }
      return a.id === alertToUpdate.id;
    };

    const updatedRows = rows.map((a: any) => {
      if (matchesSnoozeTarget(a)) {
        return { ...a, ...data };
      }
      return a;
    });
    setRows(updatedRows);
    
    if (selectedAlertObj?.id === alertToUpdate.id || selectedAlert?.id === alertToUpdate.id) {
      setSelectedAlert((prev: any) => {
        if (prev && matchesSnoozeTarget(prev)) {
          return { ...prev, ...data };
        }
        return prev;
      });
    }
  };

  // Apply filters and selections to alerts
  const getFilteredAlerts = (alerts: any[]) => {
       function applyFilters(alerts: any[]) {
      return _(alerts)
        .filter((alert) => {
          return _.isEmpty(selectedFilters.code) ? true : selectedFilters.code[alert.errorCode];
        })
        .filter((alert) => {
          return _.isEmpty(selectedFilters.type) ? true : selectedFilters.type[alert.alertType] || selectedFilters.type[alert.severity?.name];
        })
        .filter((alert) => {
          return _.isEmpty(selectedFilters.status) ? true : selectedFilters.status[alert.status];
        })
        .filter((alert) => {
          return _.isEmpty(selectedFilters.description) ? true : selectedFilters.description[alert.description];
        })
        .filter((alert) => {
          return _.isEmpty(selectedFilters.priority) ? true : selectedFilters.priority[trapPriorityTypes[alert.priority]];
        })
        .filter((alert) => {
          return _.isEmpty(selectedFilters.category) ? true : selectedFilters.category[alert.severity?.name];
        })
        .value();
    }
    function applySelections(alerts: any[]) {
      return _(alerts)
        .filter((alert: any) =>
          selections.unitId ? alert.alertItemContainerIds.unitId ? alert.alertItemContainerIds.unitId === selections.unitId : (alert.alertItemContainerIds.unitIds.length > 1 && alert.alertItemContainerIds.unitIds.includes(selections.unitId)) : true
        )
        .filter((alert: any) =>
          selections.siteId ? alert.alertItemContainerIds.siteId === selections.siteId : true
        )
        .filter((alert: any) =>
          selections.systemId ? alert.alertItemContainerIds.systemId ? alert.alertItemContainerIds.systemId === selections.systemId :
            (alert.alertItemContainerIds.systemIds.length > 1 && alert.alertItemContainerIds.systemIds.includes(selections.systemId)) : true
        )
        .filter((alert: any) =>
          selections.systemId && _.includes(selections.systemId, "_") ?
            allUnits[alert.alertItemContainerIds.unitId].line.toString() === selections.systemId.split("_")[0] && allUnits[alert.alertItemContainerIds.unitId].device === selections.systemId.split("_")[1] :
            true

        )
        .filter((alert: any) =>
          selections.customerId
            ? alert.alertItemContainerIds.customerId === selections.customerId
            : true
        )
        .value();
    }

    const updateFiltersVals = (rows: any) => {
      const errorCodeArr: any = { ...errorCodesMap };
      const statusArr: any = { ...statusesMap };
      const alertTypesArr: any = { ...alertTypeMap };
      const descriptionArr: any = { ...descriptionMap };
      const priorityArr: any = { ...priorityMap };
      const categoryArr: any = { ...categoryMap };

      for (let rowIndex in rows) {
        const { status, errorCode, alertType, description, priority, category } = rows[rowIndex];
        if (!!errorCode) { errorCodeArr[errorCode] = true; }
        if (!!status) { statusArr[status] = true; }
        if (!!alertType) { alertTypesArr[alertType] = true; }
        if (!!description) { descriptionArr[description] = true; }
        if (!!priority) { priorityArr[trapPriorityTypes[priority]] = true; }
        if (!!category) { categoryArr[category] = true; }
      }
      setErrorCodesMap(errorCodeArr);
      setStatusesMap(statusArr);
      setAlertTypeMap(alertTypesArr);
      setDescriptionMap(descriptionArr);
      setPriorityMap(priorityArr);
      setCategoryMap(categoryArr);

    };

    const filteredRows = applyFilters(applySelections(alerts));
    updateFiltersVals(filteredRows);
    return filteredRows;
  };

  const filterValues: any = {
    status: statusesMap,
    code: errorCodesMap,
    type: alertTypeMap,
    description: descriptionMap,
    priority: priorityMap,
    category: categoryMap
  };

  const onApply = (selectedFilters: any) => {
    setSelectedFilters({ ...selectedFilters });
    setOpenedFilters(false);
  };


  const getComputedDescription = (alert: any) => {
    const { description, errorDescription, alertType, type, errorCode } = alert;
    if (
      type === eventTypes.bacnetObjectReadingError ||
      type === eventTypes.bacnetObjectPriorityError
    ) {
      return alertType;
    }
    const dec = errorDescription || description;
    return errorCode ? `${errorCode} : ${dec}` : dec;
  };

  const handleSiteSelect = () => {
    if (match?.params?.notificationShortId) {
      setSelectedAlert(null);
      history.push("/alerts");
    }
  };
    

  return (
    <>
      <TopBar
        leftIconComponent={selectedAlert ? <ArrowBack /> : <MobileLogo />}
        leftAction={selectedAlert ? () => setSelectedAlert(null) : () => history.push("/dashboard")}
        screenTitle="alerts"
        applySiteTypeFiltering
        onSiteSelect={handleSiteSelect}
      />
      <div className={classes.view}>
        <div className={classes.contentArea} style={{ height: "calc(100% - 10px)", background: "white" }}>
          <div id="title" style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "53px",
            borderBottom: "solid 1px #e5e2e5",
            padding: "0 20px"
          }}>
            <div
              style={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <AlertBell style={{ width: 25, height: 25 }} />
              <Typography style={{
                marginLeft: "15px",
                fontFamily: "Roboto",
                fontSize: 20,
                fontWeight: 600,
                color: "#15111f"
              }}>{t`Alerts Log`}</Typography>
            </div>
            {match?.params?.notificationShortId && <Button variant="text" onClick={() => history.push("/alerts")}>{t`Back to all alerts`}</Button>}
          </div>
          <div style={{ display: "flex", flexFlow: "row nowrap", justifyContent: "space-between", alignItems: "center", padding: "0 20px", minHeight: 50, borderBottom: "solid 1px #e5e2e5" }} onClick={() => setOpenDatePicker(true)}>
            <Typography style={{
              fontFamily: "Roboto",
              fontSize: 16,
              fontWeight: 600,
              color: "#29132e"
            }}
            >
              {dateRange ? `${moment(dateRange?.startDate).format("MMM DD")} - ${moment(dateRange?.endDate).format("MMM DD")}` : t`All Open alerts`}
            </Typography>
            <ArrowDown
              style={{ height: 8, width: 12 }}
            />
          </div>

          <div onClick={() => setOpenedFilters(true)} style={{ display: "flex", flexFlow: "row nowrap", justifyContent: "space-between", alignItems: "center", padding: "0 20px", minHeight: 50, borderBottom: "solid 1px #e5e2e5" }}>
            <Typography style={{
              fontFamily: "Roboto",
              fontSize: 16,
              fontWeight: 600,
              color: "#29132e",
              marginRight: 18,
              whiteSpace: "nowrap"
            }}
            >{t`Filter By`}</Typography>
            <div className={classes.filterItemsContainer}>
              {Object.keys(selectedFilters.code).map((filterName: string) =>
                <div key={`filter-item-name-${filterName}`} className={classes.filterItem}>
                  <Typography className={classes.filterItemText}>{filterName}</Typography>
                  <IconButton disableRipple className={classes.filterItemIconBtn} onClick={(event: any) => { event.stopPropagation(); event.preventDefault(); delete selectedFilters.code[filterName]; setSelectedFilters({ ...selectedFilters }); }}>
                    <Close className={classes.filterItemCloseIcon} />
                  </IconButton>
                </div>
              )
              }
              {Object.keys(selectedFilters.description).map((filterName: string) =>
                <div key={`filter-item-name-${filterName}`} className={classes.filterItem}>
                  <Typography className={classes.filterItemText}>{filterName}</Typography>
                  <IconButton disableRipple className={classes.filterItemIconBtn} onClick={(event: any) => { event.stopPropagation(); event.preventDefault(); delete selectedFilters.description[filterName]; setSelectedFilters({ ...selectedFilters }); }}>
                    <Close className={classes.filterItemCloseIcon} />
                  </IconButton>
                </div>
              )
              }
              {Object.keys(selectedFilters.status).map((filterName: string) =>
                <div key={`filter-item-name-${filterName}`} className={classes.filterItem}>
                  <Typography className={classes.filterItemText}>{filterName}</Typography>
                  <IconButton disableRipple className={classes.filterItemIconBtn} onClick={(event: any) => { event.stopPropagation(); event.preventDefault(); delete selectedFilters.status[filterName]; setSelectedFilters({ ...selectedFilters }); }}>
                    <Close className={classes.filterItemCloseIcon} />
                  </IconButton>
                </div>
              )
              }
              {Object.keys(selectedFilters.type).map((filterName: string) =>
                <div key={`filter-item-name-${filterName}`} className={classes.filterItem}>
                  <Typography className={classes.filterItemText}>{filterName}</Typography>
                  <IconButton disableRipple className={classes.filterItemIconBtn} onClick={(event: any) => { event.stopPropagation(); event.preventDefault(); delete selectedFilters.type[filterName]; setSelectedFilters({ ...selectedFilters }); }}>
                    <Close className={classes.filterItemCloseIcon} />
                  </IconButton>
                </div>
              )
              }
            </div>
            <FilterList />
          </div>
          <div id="content" style={{ overflow: "auto", height: "calc(100% - 260px)", maxHeight: "calc(100% - 260px)", display: "flex", justifyContent: "center", flexFlow: "row wrap" }}>
            {isDisabled ? <CircularProgress style={{ alignSelf: "center" }} /> : !selectedSiteId ? <FilterRequire type="site" mobile/> : filteredAlerts.length === 0 ? <NoAlerts /> : <div style={{ width: "100%" }}> {filteredAlerts.map((alert: any, index: number) =>
              <div id="topRow" key={index} style={{ width: "100%", display: "flex", flexFlow: "row nowrap", minHeight: "120px", borderBottom: "solid 1px #e5e2e5", paddingLeft: 15, paddingRight: 5, paddingTop: 20 }}
                onClick={() => onRowSelect(alert)}
              >
                <div /* column 1 */ style={{ height: 22, display: "flex", alignItems: "center" }}>
                  <div className={classes.typeKey} style={{ backgroundColor: alert.severity.color }} />
                </div>
                <div /*sec column */ style={{ flex: 1, paddingLeft: 10 }}>
                  <div style={{ display: "flex", flexFlow: "row nowrap", /*height: 19, */alignItems: "baseline" }}>
                    <Typography style={{
                      flex: 1,
                      fontFamily: "Roboto",
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#545964"
                    }}>{alert.alertType}</Typography>
                    <Typography style={{
                      width: "120px",
                      fontFamily: "Roboto",
                      fontSize: 14,
                      color: "#15111f"
                    }}>{alert.time}</Typography>
                    <IconButton className={classes.iconBtn} >
                      <ArrowDown style={{ height: 8, width: 12, transform: "rotate(-90deg)" }} />
                    </IconButton>
                  </div>
                  <div id="middleRows" style={{ display: "flex", flexFlow: "column nowrap", maxWidth: "calc(100vw - 60px)" }}>
                    <div style={{ display: "flex" }}>
                      <Typography className={classes.cutLongNames} style={{ maxWidth: "50%", fontFamily: "Roboto", fontSize: 14, color: "#545964" }}>
                        {alert.type === eventTypes.deviceDisconnected && alert.status === "Closed" ? alert.deviceName : alert.unitName || "-"}
                      </Typography>
                    </div>
                    <div>
                      {alert.type === eventTypes.bacnetUnitError ? (
                        <Typography
                          className={classes.descInfo}
                          style={{ color: alert.data?.color }}
                        >
                         {alert.errorDescription}
                        </Typography>
                      ) : (
                        <Typography
                          className={classes.descInfo}
                          dangerouslySetInnerHTML={{
                            __html:
                              alert.styledDescription ||
                              getComputedDescription(alert) ||
                              '',
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div id="bottomRow" style={{ display: "flex", flexFlow: "row nowrap" }}>
                    <Typography className={clsx(classes.alertStatus, { [classes.openAlert]: alert.status === "Open", [classes.closedAlert]: alert.status === "Closed" })}>{alert.status}</Typography>
                    <Typography className={classes.clearTime}>{alert.clearTime}</Typography>
                  </div>
                </div>
              </div>
            )}
            </div>
            }
          </div>
          {opendatePicker && <DatePicker close={() => setOpenDatePicker(false)} />}
          {openFilters &&
            <Lookup
              filtersList={filterValues}
              appliedFilters={selectedFilters}
              onApply={onApply}
              onClose={() => onApply(selectedFilters)}
              title={t`Alert Log Filters`}
            />
          }
        </div>
        {selectedAlert && <AlertInfo
          alert={selectedAlert}
          setAcknowledged={setAcknowledged}
          updateAlertLocally={updateAlertLocally}
          refreshAlerts={() => setNotificationRefresh((r) => r + 1)}
        />}
      </div>
    </>
  );
};
export default Alerts;
